import React from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
} from "@mui/material";
import TriviaGameHUD from "./TriviaGameHUD";
import TriviaQuestionStage from "./TriviaQuestionStage";
import TriviaPlayerOptionCard from "./TriviaPlayerOptionCard";
import TriviaFeedbackOverlay from "./TriviaFeedbackOverlay";
import TriviaAchievementToast from "./TriviaAchievementToast";
import { useTriviaFeedbackContinueHotkeys } from "../../hooks/useTriviaFeedbackContinueHotkeys";
import { TRIVIA_QUESTION_PANE_FEEDBACK_FLEX } from "../../utils/triviaFeedbackLayout";
import {
  detectQuestionType,
  optionAnswerValue,
  optionMatchesCorrectAnswer,
  withRevealedStatValues,
} from "../../utils/triviaArcade";

const getCardStatus = (option, phase, lastResult, selectedAnswer) => {
  const value = optionAnswerValue(option);
  if (phase !== "feedback" || !lastResult) {
    return selectedAnswer === value ? "selected" : "idle";
  }

  if (optionMatchesCorrectAnswer(option, lastResult.correctAnswer)) return "correct";
  if (selectedAnswer === value) return "wrong";
  return "idle";
};

const TriviaGameScreen = ({
  sport = "mlb",
  question,
  currentIndex,
  total,
  score,
  timeLeft,
  timeLimit,
  streak,
  phase,
  lastResult,
  selectedAnswer,
  toastQueue,
  reducedMotion,
  showQuitDialog,
  error,
  onSelectAnswer,
  onQuit,
  onConfirmQuit,
  onCancelQuit,
  onDismissToast,
  onSkipFeedback,
}) => {
  const questionType = detectQuestionType(question);
  const revealStats = phase === "feedback";
  const options = revealStats
    ? withRevealedStatValues(question?.options, lastResult?.sourceData)
    : question?.options;
  const gridColumns =
    questionType === "true_false" ? "1fr 1fr" : { xs: "1fr 1fr", sm: "1fr 1fr" };

  useTriviaFeedbackContinueHotkeys({
    phase,
    onSkipFeedback,
    dialogOpen: showQuitDialog,
  });

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal,
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mx: { xs: 2, sm: 3 }, mt: { xs: 2, sm: 3 } }}>
          {error}
        </Alert>
      )}

      <Box
        data-testid="trivia-question-pane"
        data-yield="true"
        sx={{
          flex: TRIVIA_QUESTION_PANE_FEEDBACK_FLEX,
          minHeight: 0,
          overflow: "auto",
          p: { xs: 2, sm: 3 },
          pb: 1,
        }}
      >
        <TriviaGameHUD
          currentIndex={currentIndex}
          total={total}
          score={score}
          timeLeft={timeLeft}
          timeLimit={timeLimit}
          streak={streak}
          onQuit={onQuit}
          reducedMotion={reducedMotion}
          compact={revealStats}
        />

        <TriviaQuestionStage question={question} compact={revealStats} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: gridColumns,
            gap: 2,
            maxWidth: 720,
            mx: "auto",
            width: "100%",
          }}
        >
          {options?.map((option, index) => (
            <TriviaPlayerOptionCard
              key={option.playerId || option.text || index}
              option={option}
              sport={sport}
              revealStats={revealStats}
              questionSubcategory={question?.subcategory}
              status={getCardStatus(option, phase, lastResult, selectedAnswer)}
              disabled={phase !== "answering"}
              onSelect={onSelectAnswer}
            />
          ))}
        </Box>
      </Box>

      <TriviaFeedbackOverlay
        phase={phase}
        lastResult={lastResult}
        reducedMotion={reducedMotion}
        dialogOpen={showQuitDialog}
        onSkipFeedback={onSkipFeedback}
      />

      <TriviaAchievementToast queue={toastQueue} onDismiss={onDismissToast} />

      <Dialog open={showQuitDialog} onClose={onCancelQuit}>
        <DialogTitle>Quit game?</DialogTitle>
        <DialogContent>
          <Typography>
            Your current streak will be lost if you quit now.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancelQuit}>Keep playing</Button>
          <Button color="error" onClick={onConfirmQuit}>
            Quit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TriviaGameScreen;
